import React from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import { DashboardLayout } from "@/components/DashboardLayout";
import { Card, LoadingBlock, EmptyState } from "@/components/common";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { euro, formatDate, BOOKING_STATUS_LABELS } from "@/lib/format";
import { MapPin, UserRound, CalendarClock, CheckCircle2, Phone } from "lucide-react";

export default function Incarichi() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["prof-bookings", "confirmed"], queryFn: async () => (await api.get("/professional/bookings?status=confermata")).data });
  const { data: allData } = useQuery({ queryKey: ["prof-bookings"], queryFn: async () => (await api.get("/professional/bookings")).data });
  const confirmed = data?.bookings || [];
  const completed = (allData?.bookings || []).filter((b) => b.booking_status === "completata");

  const complete = async (id) => { try { await api.post(`/professional/bookings/${id}/complete`); toast.success("Prestazione completata."); qc.invalidateQueries({ queryKey: ["prof-bookings"] }); } catch (e) { toast.error(apiError(e)); } };

  const Item = ({ b, done }) => (
    <Card data-testid={`incarico-${b.id}`}>
      <div className="flex flex-col sm:flex-row justify-between gap-3">
        <div className="space-y-1">
          <p className="font-bold text-[#102A2A]">{b.service_name}</p>
          <p className="text-sm text-[#5C6B68] flex items-center gap-1"><UserRound className="h-4 w-4" /> {b.recipient_name}</p>
          <p className="text-sm text-[#5C6B68] flex items-center gap-1"><CalendarClock className="h-4 w-4" /> {formatDate(b.scheduled_date)} · {b.start_time}-{b.end_time}</p>
          <p className="text-sm text-[#5C6B68] flex items-center gap-1"><MapPin className="h-4 w-4" /> {b.address}</p>
          <p className="text-sm text-[#5C6B68] flex items-center gap-1"><Phone className="h-4 w-4" /> {b.contact_phone}</p>
        </div>
        <div className="flex flex-col items-start sm:items-end gap-2">
          <StatusBadge status={b.booking_status} label={BOOKING_STATUS_LABELS[b.booking_status]} />
          <span className="font-bold text-[#087A73]">{euro(b.professional_amount_cents)}</span>
          {!done && <Button data-testid={`complete-${b.id}`} onClick={() => complete(b.id)} className="bg-[#087A73] text-white rounded-xl h-10 px-4 text-sm"><CheckCircle2 className="mr-1 h-4 w-4" /> Segna completata</Button>}
        </div>
      </div>
    </Card>
  );

  return (
    <DashboardLayout title="Incarichi confermati">
      {isLoading ? <LoadingBlock /> : (
        <div className="space-y-6">
          <div>
            <h3 className="font-bold text-[#102A2A] mb-3">In programma</h3>
            {confirmed.length === 0 ? <EmptyState title="Nessun incarico confermato" subtitle="Gli incarichi accettati appariranno qui." /> :
              <div className="space-y-3">{confirmed.map((b) => <Item key={b.id} b={b} />)}</div>}
          </div>
          {completed.length > 0 && (
            <div>
              <h3 className="font-bold text-[#102A2A] mb-3">Completati</h3>
              <div className="space-y-3">{completed.map((b) => <Item key={b.id} b={b} done />)}</div>
            </div>
          )}
        </div>
      )}
    </DashboardLayout>
  );
}
